import { Injectable } from '@angular/core';

export interface ChatTranslations {
  title: string;
  placeholder: string;
  welcomeMessage: string;
  typingIndicator: string;
  errorMessage: string;
  newChatTitle: string;
  feedback: {
    rateButton: string;
    modalTitle: string;
    ratingQuestion: string;
    commentLabel: string;
    commentPlaceholder: string;
    cancelButton: string;
    submitButton: string;
    sendingFeedback: string;
    feedbackSent: string;
  };
  buttons: {
    send: string;
    close: string;
    minimize: string;
    expand: string;
    fullscreen: string;
    exitFullscreen: string;
    resetSize: string;
    newChat: string;
    selectEmptyChat: string;
    deleteConversation?: string;
    confirmDelete?: string;
    cancelDelete?: string;
    previousPage?: string;
    nextPage?: string;
  };
  confirmations?: {
    deleteConversation: string;
  };
  attachments?: {
    addAttachment: string;
    removeFile: string;
    maxFilesReached: string;
    fileTooLarge: string;
    invalidFileType: string;
    fileAlreadyAttached: string;
  };
  news?: {
    bubbleText: string;
    modalTitle: string;
    introHighlight: string;
    introText: string;
    featuresTitle: string;
    attachmentsTitle: string;
    attachmentsDesc: string;
    queriesTitle: string;
    queriesDesc: string;
    logisticsTitle: string;
    logisticsItems: string[];
    ctaText: string;
    signature: string;
    startButton: string;
  };
  examplePrompts?: {
    backorder: string;
    invoiceStatus: string;
    workOrders: string;
    creditLimit: string;
    addDescription: string;
    breakSupersession: string;
  };
  suggestionsHint?: {
    navigate: string;
    select: string;
    close: string;
  };
  expired?: {
    message: string;
    placeholder: string;
    newChat: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ChatTranslationService {
  private translations: { [key: string]: ChatTranslations } = {
    'pt-BR': {
      title: 'Wings AI Assistant',
      placeholder: 'Digite sua mensagem...',
      welcomeMessage: 'Olá! Sou o assistente IA do Wings. Como posso ajudá-lo hoje?',
      typingIndicator: 'Digitando...',
      errorMessage: 'Erro ao obter resposta do assistente',
      newChatTitle: 'Nova Conversa',
      feedback: {
        rateButton: 'Avaliar',
        modalTitle: 'Avaliar Resposta',
        ratingQuestion: 'Quão útil foi esta resposta?',
        commentLabel: 'Comentários adicionais (opcional):',
        commentPlaceholder: 'Diga-nos como podemos melhorar...',
        cancelButton: 'Cancelar',
        submitButton: 'Enviar Feedback',
        sendingFeedback: 'Enviando...',
        feedbackSent: 'Feedback enviado'
      },
      buttons: {
        send: 'Enviar',
        close: 'Fechar',
        minimize: 'Minimizar',
        expand: 'Expandir',
        fullscreen: 'Tela Cheia',
        exitFullscreen: 'Sair da Tela Cheia',
        resetSize: 'Redefinir Tamanho',
        newChat: 'Nova Conversa',
        selectEmptyChat: 'Selecionar conversa vazia existente',
        deleteConversation: 'Excluir conversa',
        confirmDelete: 'Confirmar exclusão',
        cancelDelete: 'Cancelar',
        previousPage: 'Página anterior',
        nextPage: 'Próxima página'
      },
      confirmations: {
        deleteConversation: 'Tem certeza que deseja excluir esta conversa? Ela será removida apenas da sua visualização e não será deletada do banco de dados.'
      },
      attachments: {
        addAttachment: 'Adicionar anexo',
        removeFile: 'Remover arquivo',
        maxFilesReached: 'Limite máximo de arquivos atingido',
        fileTooLarge: 'Arquivo muito grande (máx. 10MB)',
        invalidFileType: 'Tipo de arquivo não permitido',
        fileAlreadyAttached: 'Arquivo já anexado'
      },
      news: {
        bubbleText: 'Venha conhecer as novidades do Wings Agent!',
        modalTitle: '🎉 Novidades do Wings Agent!',
        introHighlight: 'É com grande satisfação que anunciamos o lançamento do novo ecossistema <strong>Wings Agent</strong>, uma assistência virtual inteligente projetada para otimizar nossos processos e facilitar seu trabalho diário com o sistema Wings.',
        introText: 'Integrado diretamente ao nosso ambiente de chat, o Wings Agent está pronto para ajudar a executar tarefas, consultar informações e gerenciar tickets de forma rápida e conversacional.',
        featuresTitle: 'O que o Wings Agent pode fazer por você?',
        attachmentsTitle: '📎 Anexos Inteligentes',
        attachmentsDesc: 'Envie arquivos diretamente no chat! Suportamos <strong>PDFs, planilhas Excel, documentos Word, CSVs e imagens</strong>. O agente pode ler e analisar o conteúdo dos seus documentos para fornecer respostas contextualizadas.',
        queriesTitle: '📚 Consultas ao Sistema Wings',
        queriesDesc: 'Tire dúvidas sobre as regras de negócio e a lógica do sistema Wings de maneira instantânea.',
        logisticsTitle: '🚚 Operações de Logística e Estoque',
        logisticsItems: [
          'Verifique o status e processe notas fiscais parciais (ASN)',
          'Consulte ordens de serviço (workorders) em andamento',
          'Verifique o limite de crédito de dealers',
          'Adicione ou atualize descrições de peças',
          'Gerencie a disponibilidade e ciclo de vida de peças',
          'Quebre a relação de supersessão entre peças'
        ],
        ctaText: '<strong>O Wings Agent foi criado para ser seu principal ponto de contato</strong>, automatizando tarefas rotineiras e fornecendo as informações que você precisa, quando precisa.',
        signature: 'Atenciosamente,<br><strong>Time Wings! 💙</strong>',
        startButton: 'Começar a usar!'
      },
      examplePrompts: {
        backorder: "Me explique a regra de negócio para o processo de 'backorder'.",
        invoiceStatus: 'Verificar o status da nota fiscal 12345 para o Brasil.',
        workOrders: 'Quais as ordens de serviço em aberto para o dealer 74012?',
        creditLimit: 'Consulte o limite de crédito do dealer 64251 no Brasil.',
        addDescription: "Adicionar a descrição 'MOTOR DE ARRANQUE' para a peça XYZ-123 no Brasil.",
        breakSupersession: 'Quebrar a supersessão entre a peça A-100 e B-200.'
      },
      suggestionsHint: {
        navigate: 'para navegar',
        select: 'para selecionar',
        close: 'para fechar'
      },
      expired: {
        message: 'Esta conversa expirou por inatividade.',
        placeholder: 'Conversa expirada — inicie uma nova conversa',
        newChat: 'Nova Conversa'
      }
    },
    'en': {
      title: 'Wings AI Assistant',
      placeholder: 'Type your message...',
      welcomeMessage: 'Hello! I am the Wings AI assistant. How can I help you today?',
      typingIndicator: 'Typing...',
      errorMessage: 'Error getting assistant response',
      newChatTitle: 'New Chat',
      feedback: {
        rateButton: 'Rate',
        modalTitle: 'Rate Response',
        ratingQuestion: 'How helpful was this response?',
        commentLabel: 'Additional comments (optional):',
        commentPlaceholder: 'Tell us how we can improve...',
        cancelButton: 'Cancel',
        submitButton: 'Submit Feedback',
        sendingFeedback: 'Sending...',
        feedbackSent: 'Feedback sent'
      },
      buttons: {
        send: 'Send',
        close: 'Close',
        minimize: 'Minimize',
        expand: 'Expand',
        fullscreen: 'Fullscreen',
        exitFullscreen: 'Exit Fullscreen',
        resetSize: 'Reset Size',
        newChat: 'New Chat',
        selectEmptyChat: 'Select existing empty chat',
        deleteConversation: 'Delete conversation',
        confirmDelete: 'Confirm deletion',
        cancelDelete: 'Cancel',
        previousPage: 'Previous page',
        nextPage: 'Next page'
      },
      confirmations: {
        deleteConversation: 'Are you sure you want to delete this conversation? It will only be removed from your view and will not be deleted from the database.'
      },
      attachments: {
        addAttachment: 'Add attachment',
        removeFile: 'Remove file',
        maxFilesReached: 'Maximum file limit reached',
        fileTooLarge: 'File too large (max. 10MB)',
        invalidFileType: 'File type not allowed',
        fileAlreadyAttached: 'File already attached'
      },
      news: {
        bubbleText: 'Come discover the new features of Wings Agent!',
        modalTitle: '🎉 Wings Agent News!',
        introHighlight: 'We are pleased to announce the launch of the new <strong>Wings Agent</strong> ecosystem, an intelligent virtual assistant designed to optimize our processes and facilitate your daily work with the Wings system.',
        introText: 'Integrated directly into our chat environment, Wings Agent is ready to help execute tasks, query information, and manage tickets quickly and conversationally.',
        featuresTitle: 'What can Wings Agent do for you?',
        attachmentsTitle: '📎 Smart Attachments',
        attachmentsDesc: 'Send files directly in chat! We support <strong>PDFs, Excel spreadsheets, Word documents, CSVs, and images</strong>. The agent can read and analyze the content of your documents to provide contextualized responses.',
        queriesTitle: '📚 Wings System Queries',
        queriesDesc: 'Get instant answers about business rules and Wings system logic.',
        logisticsTitle: '🚚 Logistics and Inventory Operations',
        logisticsItems: [
          'Check status and process partial invoices (ASN)',
          'Query work orders in progress',
          'Check dealer credit limits',
          'Add or update part descriptions',
          'Manage part availability and lifecycle',
          'Break supersession relationships between parts'
        ],
        ctaText: '<strong>Wings Agent was created to be your main point of contact</strong>, automating routine tasks and providing the information you need, when you need it.',
        signature: 'Best regards,<br><strong>Wings Team! 💙</strong>',
        startButton: 'Start using!'
      },
      examplePrompts: {
        backorder: "Explain the business rule for the 'backorder' process.",
        invoiceStatus: 'Check the status of invoice 12345 for Brazil.',
        workOrders: 'What are the open work orders for dealer 74012?',
        creditLimit: 'Check the credit limit for dealer 64251 in Brazil.',
        addDescription: "Add the description 'STARTER MOTOR' for part XYZ-123 in Brazil.",
        breakSupersession: 'Break the supersession between part A-100 and B-200.'
      },
      suggestionsHint: {
        navigate: 'to navigate',
        select: 'to select',
        close: 'to close'
      },
      expired: {
        message: 'This conversation has expired due to inactivity.',
        placeholder: 'Conversation expired — start a new chat',
        newChat: 'New Chat'
      }
    },
    'es': {
      title: 'Wings AI Assistant',
      placeholder: 'Escribe tu mensaje...',
      welcomeMessage: '¡Hola! Soy el asistente IA de Wings. ¿Cómo puedo ayudarte hoy?',
      typingIndicator: 'Escribiendo...',
      errorMessage: 'Error al obtener respuesta del asistente',
      newChatTitle: 'Nueva Conversación',
      feedback: {
        rateButton: 'Evaluar',
        modalTitle: 'Evaluar Respuesta',
        ratingQuestion: '¿Qué tan útil fue esta respuesta?',
        commentLabel: 'Comentarios adicionales (opcional):',
        commentPlaceholder: 'Díganos cómo podemos mejorar...',
        cancelButton: 'Cancelar',
        submitButton: 'Enviar Comentarios',
        sendingFeedback: 'Enviando...',
        feedbackSent: 'Comentarios enviados'
      },
      buttons: {
        send: 'Enviar',
        close: 'Cerrar',
        minimize: 'Minimizar',
        expand: 'Expandir',
        fullscreen: 'Pantalla Completa',
        exitFullscreen: 'Salir de Pantalla Completa',
        resetSize: 'Restablecer Tamaño',
        newChat: 'Nova Conversa',
        selectEmptyChat: 'Seleccionar conversación vacía existente',
        deleteConversation: 'Eliminar conversación',
        confirmDelete: 'Confirmar eliminación',
        cancelDelete: 'Cancelar',
        previousPage: 'Página anterior',
        nextPage: 'Página siguiente'
      },
      confirmations: {
        deleteConversation: '¿Está seguro de que desea eliminar esta conversación? Solo se eliminará de su vista y no se eliminará de la base de datos.'
      },
      attachments: {
        addAttachment: 'Agregar archivo adjunto',
        removeFile: 'Eliminar archivo',
        maxFilesReached: 'Límite máximo de archivos alcanzado',
        fileTooLarge: 'Archivo demasiado grande (máx. 10MB)',
        invalidFileType: 'Tipo de archivo no permitido',
        fileAlreadyAttached: 'Archivo ya adjunto'
      },
      news: {
        bubbleText: '¡Ven a conocer las novedades de Wings Agent!',
        modalTitle: '🎉 ¡Novedades de Wings Agent!',
        introHighlight: 'Nos complace anunciar el lanzamiento del nuevo ecosistema <strong>Wings Agent</strong>, un asistente virtual inteligente diseñado para optimizar nuestros procesos y facilitar su trabajo diario con el sistema Wings.',
        introText: 'Integrado directamente en nuestro entorno de chat, Wings Agent está listo para ayudar a ejecutar tareas, consultar información y gestionar tickets de forma rápida y conversacional.',
        featuresTitle: '¿Qué puede hacer Wings Agent por usted?',
        attachmentsTitle: '📎 Archivos Adjuntos Inteligentes',
        attachmentsDesc: '¡Envíe archivos directamente en el chat! Soportamos <strong>PDFs, hojas de cálculo Excel, documentos Word, CSVs e imágenes</strong>. El agente puede leer y analizar el contenido de sus documentos para proporcionar respuestas contextualizadas.',
        queriesTitle: '📚 Consultas al Sistema Wings',
        queriesDesc: 'Resuelva dudas sobre las reglas de negocio y la lógica del sistema Wings de manera instantánea.',
        logisticsTitle: '🚚 Operaciones de Logística e Inventario',
        logisticsItems: [
          'Verifique el estado y procese facturas parciales (ASN)',
          'Consulte órdenes de trabajo en curso',
          'Verifique el límite de crédito de concesionarios',
          'Agregue o actualice descripciones de piezas',
          'Gestione la disponibilidad y ciclo de vida de piezas',
          'Rompa la relación de supersesión entre piezas'
        ],
        ctaText: '<strong>Wings Agent fue creado para ser su principal punto de contacto</strong>, automatizando tareas rutinarias y proporcionando la información que necesita, cuando la necesita.',
        signature: 'Atentamente,<br><strong>¡Equipo Wings! 💙</strong>',
        startButton: '¡Empezar a usar!'
      },
      examplePrompts: {
        backorder: "Explíqueme la regla de negocio para el proceso de 'backorder'.",
        invoiceStatus: 'Verificar el estado de la factura 12345 para Brasil.',
        workOrders: '¿Cuáles son las órdenes de trabajo abiertas para el concesionario 74012?',
        creditLimit: 'Consulte el límite de crédito del concesionario 64251 en Brasil.',
        addDescription: "Agregar la descripción 'MOTOR DE ARRANQUE' para la pieza XYZ-123 en Brasil.",
        breakSupersession: 'Romper la supersesión entre la pieza A-100 y B-200.'
      },
      suggestionsHint: {
        navigate: 'para navegar',
        select: 'para seleccionar',
        close: 'para cerrar'
      },
      expired: {
        message: 'Esta conversación ha expirado por inactividad.',
        placeholder: 'Conversación expirada — inicie una nueva conversación',
        newChat: 'Nueva Conversación'
      }
    }
  };

  public getTranslations(language: string): ChatTranslations {
    // Normalizar o idioma
    const normalizedLang = this.normalizeLanguage(language);
    return this.translations[normalizedLang] || this.translations['en'];
  }

  private normalizeLanguage(language: string): string {
    if (!language) return 'en';
    
    const lang = language.toLowerCase();
    
    if (lang.includes('pt')) return 'pt-BR';
    if (lang.includes('es')) return 'es';
    if (lang.includes('en')) return 'en';
    
    return 'en'; // fallback
  }

  public getAvailableLanguages(): string[] {
    return Object.keys(this.translations);
  }
}
